import { useQuery } from "@apollo/client/react";
import { graphql } from "@/gql";
import type { Permission } from "@/gql/graphql";
import type { JSX, ReactNode } from "react";

export const GET_MY_PERMISSIONS = graphql(`
  query GetMyPermissions {
    me {
      permissions
    }
  }
`);

export interface Permissions {
  /** Nothing is permitted until the answer arrives; check this to tell the two apart. */
  loading: boolean;
  can: (permission: Permission) => boolean;
}

export function usePermissions(): Permissions {
  const { data, loading } = useQuery(GET_MY_PERMISSIONS);
  return {
    loading,
    can: (permission) => data?.me?.permissions?.includes(permission) ?? false,
  };
}

/**
 * Whether the caller holds a permission, as the backend resolved it.
 *
 * False while the answer is still loading, so a control the caller turns out
 * not to have is never shown and then taken away.
 */
export function useCan(permission: Permission): boolean {
  return usePermissions().can(permission);
}

export interface IfPermittedProps {
  permission: Permission;
  children: ReactNode;
}

/** `useCan` for call sites that only wrap JSX. */
export function IfPermitted({
  permission,
  children,
}: IfPermittedProps): JSX.Element | null {
  return useCan(permission) ? <>{children}</> : null;
}
